import React from 'react';
import { Box, Grid, HStack, Skeleton, SkeletonCircle, Stack } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';

export type DataSkeletonVariant = 'table' | 'cards' | 'form' | 'detail';

export interface DataSkeletonProps {
  variant?: DataSkeletonVariant;
  rows?: number;
  columns?: number;
  label?: string;
}

export function DataSkeleton({
  variant = 'table',
  rows = 6,
  columns = 4,
  label = 'Carregando dados',
}: DataSkeletonProps) {
  const reducedMotion = useReducedMotion();
  const skeletonMotion = reducedMotion ? { animation: 'none' } : { speed: 0.8 };

  if (variant === 'cards') {
    return (
      <Grid
        role="status"
        aria-label={label}
        aria-busy="true"
        data-testid="data-skeleton"
        templateColumns="repeat(auto-fit, minmax(220px, 1fr))"
        gap={5}
      >
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={`card-${index + 1}`} background="white" borderRadius="lg" p={5}>
            <HStack mb={4}>
              <SkeletonCircle size="10" {...skeletonMotion} />
              <Skeleton height="18px" width="55%" {...skeletonMotion} />
            </HStack>
            <Skeleton height="72px" borderRadius="md" {...skeletonMotion} />
          </Box>
        ))}
      </Grid>
    );
  }

  if (variant === 'form' || variant === 'detail') {
    return (
      <Stack
        role="status"
        aria-label={label}
        aria-busy="true"
        data-testid="data-skeleton"
        spacing={5}
        background="white"
        borderRadius="lg"
        p={6}
      >
        <Skeleton height="28px" width="35%" {...skeletonMotion} />
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton
            key={`field-${index + 1}`}
            height={variant === 'form' ? '52px' : '24px'}
            width={index % 2 === 0 ? '100%' : '72%'}
            {...skeletonMotion}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      role="status"
      aria-label={label}
      aria-busy="true"
      data-testid="data-skeleton"
      spacing={0}
      background="white"
      borderRadius="md"
      overflow="hidden"
    >
      <HStack spacing={4} px={5} py={4} background="gray.50">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={`heading-${index + 1}`} height="16px" flex="1" {...skeletonMotion} />
        ))}
      </HStack>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <HStack key={`row-${rowIndex + 1}`} spacing={4} px={5} py={4} borderTopWidth="1px">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={`cell-${rowIndex + 1}-${columnIndex + 1}`}
              height="18px"
              flex="1"
              {...skeletonMotion}
            />
          ))}
        </HStack>
      ))}
    </Stack>
  );
}
